import React, { useEffect, useState, useRef } from "react";
import "swiper/css";
import "swiper/css/navigation";
import DashboardSuggestion from "./DashboardSuggestion";
import DashboardPastPractices from "./DashboardPastPractices";
import HeaderImage from "@assets/images/mindFulnessHeader.png";
import MainHeader from "@components/ui/MainHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { fetchMindfulnessData } from "api/mindfulness";
import { toast } from "react-toastify";
import { useLoaderSlides } from "data/uiData/LoadersData";
import ReacreationLoading from "@components/ui/RecreationLoading";
import usePlanTimer from "@utils/DietPlanProgress";

const MindfulnessDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState("Weekly Overview");
  const [adviseData, setAdviseData] = useState(null);
  const [filterRecord, setFilterRecord] = useState(null);
  const [fetchHistory, setFetchHistory] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const mindfulnessSlides = useLoaderSlides("mindfulness");
  const [planInitiateTime, setPlanInitiateTime] = useState(null);

  const { timeLeft, setTimeLeft, stopCountdown } = usePlanTimer(
    planInitiateTime,
    120
  );
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!location?.state) {
      navigate(-1);
    }
  }, [location, navigate]);

  const fetchData = async (loadHIstory = false) => {
    try {
      const { data, loading, start_time } = await fetchMindfulnessData();
      if (!data && !loading) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/mindfulness");
        return;
      }

      if (isMountedRef.current) {
        const loadingStatus = loading;

        setIsPlanLoading(loadingStatus);
        setAdviseData(data?.daily_practice);
        setPlanInitiateTime(start_time);
        setFirstLoad(false);
      }
      if (loadHIstory) {
        setFetchHistory(!fetchHistory);
      }
    } catch (error) {
      console.error(
        "Error fetching Data:",
        error.response ? error.response.data : error.message
      );
      if (isMountedRef.current) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/mindfulness");
        setFirstLoad(false);
      }
    }
  };

  const handleFailedPlan = () => {
    // toast.error(t("Failed to create plan! , please try again."));
    if (adviseData) {
      setIsPlanLoading(false);
    } else {
      navigate("/mindfulness");
      return;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Cleanup effect when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (location.state) {
    return (
      <div>
        {firstLoad ? (
          <ActualDietPlanLoader mindfulness={true} />
        ) : !adviseData && isPlanLoading ? (
          <ReacreationLoading
            timeLeft={timeLeft}
            minuteSet={120}
            data={mindfulnessSlides}
            type="mindfulness"
            loaderHeading={t("mindfulnessLoader.mealPlanProgress")}
            loaderSubheading={t("mindfulnessLoader.analyzingPlan")}
            onComplete={() => fetchData(true)}
            onFailed={handleFailedPlan}
            stopCountdown={() => stopCountdown()}
            setTimeLeft={setTimeLeft}
          />
        ) : (
          <>
            <MainHeader
              heading={t("mindfulnessCoachPreferences.mindfulnessCoachHeading")}
              subheading={t(
                "mindfulnessCoachPreferences.mindfulnessCoachSubHeading"
              )}
              description={t(
                "mindfulnessCoachPreferences.mindfulnessCoachDescription"
              )}
              Image={HeaderImage}
            />
            {isPlanLoading && (
              <div className="mt-6">
                <ReacreationLoading
                  timeLeft={timeLeft}
                  minuteSet={120}
                  data={mindfulnessSlides}
                  type="mindfulness"
                  loaderHeading={t("mindfulnessLoader.mealPlanProgress")}
                  loaderSubheading={t("mindfulnessLoader.analyzingPlan")}
                  onComplete={() => fetchData(true)}
                  onFailed={handleFailedPlan}
                  stopCountdown={() => stopCountdown()}
                  setTimeLeft={setTimeLeft}
                  recreateLoading={true}
                />
              </div>
            )}
            <div className="flex bg-white rounded-full border border-[#E9E9E9] w-[fit-content] gap-0 md:gap-2 mt-7 shadow-md">
              {["Daily Overview", "Weekly Overview"]?.map((item, index) => {
                const isActive = item === tab;
                return (
                  <button
                    key={item}
                    onClick={() => setTab(item)}
                    className={`relative px-6 py-3 text-sm rounded-full capitalize transition-all duration-300  text-center ${
                      isActive ? "text-white" : "text-black bg-white"
                    }`}
                    style={{
                      background: isActive
                        ? "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)"
                        : "transparent",
                    }}
                  >
                    <span className="text-[12px] md:text-[14px] xl:text-[16px] font-[500] font-[Lato]">
                      {t(`mindfulnessCoachPreferences.${item}`)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-7 gap-5 my-7">
              <div className="lg:col-span-7">
                <DashboardSuggestion
                  adviseData={adviseData}
                  setAdviseData={setAdviseData}
                  setFilterRecord={setFilterRecord}
                  tab={tab}
                  isPlanLoading={isPlanLoading}
                />
              </div>
            </div>
            <DashboardPastPractices
              filterRecord={filterRecord}
              setFilterRecord={setFilterRecord}
              fetchHistory={fetchHistory}
            />
          </>
        )}
      </div>
    );
  }
};

export default MindfulnessDashboard;
